/**
 * FairDimensionBars - Horizontal bar chart showing F/A/I/R scores
 *
 * Displays estimated FAIR dimension scores from structural analysis.
 */

import React from 'react';
import type { FairDimensionScores } from './types';

interface FairDimensionBarsProps {
  scores: FairDimensionScores;
  loading?: boolean;
}

interface DimensionConfig {
  key: keyof FairDimensionScores;
  label: string;
  letter: string;
  color: string;
}

const DIMENSIONS: DimensionConfig[] = [
  { key: 'findability', label: 'Findability', letter: 'F', color: '#60a5fa' },
  { key: 'accessibility', label: 'Accessibility', letter: 'A', color: '#34d399' },
  { key: 'interoperability', label: 'Interoperability', letter: 'I', color: '#fbbf24' },
  { key: 'reusability', label: 'Reusability', letter: 'R', color: '#f472b6' },
];

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Strong';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Moderate';
  if (score >= 20) return 'Weak';
  return 'Minimal';
}

export const FairDimensionBars: React.FC<FairDimensionBarsProps> = ({ scores, loading }) => {
  if (loading) {
    return (
      <div className="ka-fair-bars">
        <div className="ka-fair-bars-header">
          <h4 className="ka-section-title">FAIR Dimension Scores</h4>
          <span className="ka-badge ka-badge-muted">Analyzing...</span>
        </div>
        {DIMENSIONS.map(d => (
          <div key={d.key} className="ka-fair-bar-row">
            <span className="ka-fair-letter" style={{ color: d.color }}>{d.letter}</span>
            <span className="ka-fair-label">{d.label}</span>
            <div className="ka-fair-bar-track">
              <div className="ka-fair-bar-fill ka-skeleton" style={{ width: '60%' }} />
            </div>
            <span className="ka-fair-score">--</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="ka-fair-bars">
      <div className="ka-fair-bars-header">
        <h4 className="ka-section-title">FAIR Dimension Scores</h4>
        <span className="ka-badge ka-badge-muted">Estimated from structural analysis</span>
      </div>
      {DIMENSIONS.map(d => {
        const score = scores[d.key];
        return (
          <div key={d.key} className="ka-fair-bar-row">
            <span className="ka-fair-letter" style={{ color: d.color }}>{d.letter}</span>
            <span className="ka-fair-label">{d.label}</span>
            <div className="ka-fair-bar-track">
              <div
                className="ka-fair-bar-fill"
                style={{ width: `${score}%`, backgroundColor: d.color }}
              />
            </div>
            <span className="ka-fair-score">{score}</span>
            <span className="ka-fair-score-label">{getScoreLabel(score)}</span>
          </div>
        );
      })}
    </div>
  );
};
